import { makeAutoObservable } from "mobx";

/** The last stretch of a timer fades the volume out */
export const FADE_MS = 10_000;

export type SleepTimerMode =
    | { kind: "minutes"; remainingMs: number; totalMs: number }
    | { kind: "episode-end"; episodeId: string };

export interface SleepTimerTick {
    /** 0..1, multiply the listener's volume by this */
    volumeFactor: number;
    /** True once, when the timer has run out and playback must stop */
    stop: boolean;
}

/**
 * Stops playback after a while or at the end of the episode. Counts only the time the
 * listener is actually playing, so pausing pauses the countdown. Lives in memory only.
 */
export class SleepTimerStore {
    mode: SleepTimerMode | null = null;

    constructor() {
        makeAutoObservable(this);
    }

    get isActive() {
        return this.mode !== null;
    }

    get remainingMs(): number | null {
        return this.mode?.kind === "minutes" ? this.mode.remainingMs : null;
    }

    start(minutes: number) {
        const totalMs = Math.max(1, minutes) * 60_000;
        this.mode = { kind: "minutes", remainingMs: totalMs, totalMs };
    }

    startAtEpisodeEnd(episodeId: string) {
        this.mode = { kind: "episode-end", episodeId };
    }

    extend(minutes: number) {
        if (this.mode?.kind !== "minutes") return;
        const extra = minutes * 60_000;
        this.mode = { ...this.mode, remainingMs: this.mode.remainingMs + extra, totalMs: this.mode.totalMs + extra };
    }

    cancel() {
        this.mode = null;
    }

    /** Playback ended on its own; an end-of-episode timer has done its job */
    episodeEnded(episodeId: string) {
        if (this.mode?.kind === "episode-end" && this.mode.episodeId === episodeId) {
            this.mode = null;
        }
    }

    /**
     * Advance the countdown by `elapsedMs`. `currentEpisodeId` is the loaded episode, so an
     * end-of-episode timer is dropped when the listener moves on to another one.
     */
    tick(elapsedMs: number, isPlaying: boolean, currentEpisodeId: string | null): SleepTimerTick {
        const mode = this.mode;
        if (!mode) return { volumeFactor: 1, stop: false };

        if (mode.kind === "episode-end") {
            if (currentEpisodeId !== mode.episodeId) this.mode = null;
            return { volumeFactor: 1, stop: false };
        }

        if (!isPlaying) {
            return { volumeFactor: this.factor(mode.remainingMs), stop: false };
        }

        const remainingMs = mode.remainingMs - elapsedMs;
        if (remainingMs <= 0) {
            this.mode = null;
            return { volumeFactor: 0, stop: true };
        }

        this.mode = { ...mode, remainingMs };
        return { volumeFactor: this.factor(remainingMs), stop: false };
    }

    private factor(remainingMs: number) {
        return remainingMs >= FADE_MS ? 1 : Math.max(0, remainingMs / FADE_MS);
    }
}
